import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LOCALES, pickText, type Locale } from "@heylife/i18n";
import { addDays, api, fmtDate, isoDay, scopeQuery, useScope, type Localized } from "../api";
import { useT } from "../i18n";
import { isValidSlug, slugify, useSlug } from "../slug";
import { ColorPicker, ConfirmDelete, Field, LocalizedField, Msg, SlugInput, Toggle, THEMES, useFlash } from "../ui";
import { Challenges, Cohorts } from "./Challenges";

export const CONTENT_TABS = ["dailyWords", "dailyPrayers", "oneThings", "paths", "shots", "seasons", "challenges", "cohorts"] as const;
export type ContentTab = (typeof CONTENT_TABS)[number];

export function AdminContent({ tab }: { tab: ContentTab }) {
  const { t } = useT();
  const navigate = useNavigate();
  const scope = useScope();
  const caps = useQuery({
    queryKey: ["admin", "capabilities", scope.churchId],
    queryFn: () => api<{ contentTabs: Record<string, boolean> }>(`/admin/capabilities${scopeQuery(scope, "church")}`),
    staleTime: 30_000,
  });
  const tabs = CONTENT_TABS.filter((k) => caps.data?.contentTabs[k] !== false);
  const activeTab = tabs.includes(tab) ? tab : (tabs[0] ?? "dailyWords");
  return (
    <>
      <h1>{t("admin.content.title")}</h1>
      <div className="tabs">
        {tabs.map((k) => (
          <button key={k} className={k === activeTab ? "active" : ""} onClick={() => navigate({ to: "/admin/content", search: { tab: k } })}>
            {t(`admin.content.tabs.${k}`)}
          </button>
        ))}
      </div>
      {activeTab === "dailyWords" ? <Dated kind="daily-words" /> : null}
      {activeTab === "dailyPrayers" ? <Dated kind="daily-prayers" /> : null}
      {activeTab === "oneThings" ? <Dated kind="one-things" /> : null}
      {activeTab === "paths" ? <Paths /> : null}
      {activeTab === "shots" ? <Shots /> : null}
      {activeTab === "seasons" ? <Seasons /> : null}
      {activeTab === "challenges" ? <Challenges /> : null}
      {activeTab === "cohorts" ? <Cohorts /> : null}
    </>
  );
}

/* ------------------------------------------------------------------ dated content */

type DatedKind = "daily-words" | "daily-prayers" | "one-things";
interface DatedRow {
  id: string;
  churchId: string | null;
  locale: Locale;
  publishedOn: string;
  verseRef?: string;
  verseText?: string;
  impulse?: string;
  voice?: string | null;
  title?: string;
  body?: string;
  durationSec?: number;
  text?: string;
}

const preview = (kind: DatedKind, r: DatedRow) => (kind === "daily-words" ? `${r.verseRef} · ${r.impulse}` : kind === "daily-prayers" ? `${r.title} · ${r.body}` : r.text) ?? "";

function Dated({ kind }: { kind: DatedKind }) {
  const { t, locale } = useT();
  const scope = useScope();
  const qc = useQueryClient();
  const [weeks, setWeeks] = useState(3);
  const today = isoDay(new Date());
  const from = addDays(today, -7);
  const to = addDays(today, weeks * 7);
  const key = ["admin", "content", kind, scope.churchId, from, to];
  const q = useQuery({ queryKey: key, queryFn: () => api<{ rows: DatedRow[]; globalRows: DatedRow[] }>(`/admin/content/${kind}${scopeQuery(scope, "scope", { from, to })}`) });
  const [editing, setEditing] = useState<{ day: string; locale: Locale } | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "content", kind] });

  const days: string[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) days.push(d);
  const own = (day: string, loc: Locale) => q.data?.rows.find((r) => r.publishedOn === day && r.locale === loc);
  const global = (day: string, loc: Locale) => q.data?.globalRows.find((r) => r.publishedOn === day && r.locale === loc);

  return (
    <section className="panel">
      <div className="toolbar">
        <span className="muted">
          {t("admin.content.window")}: {fmtDate(from, locale)} – {fmtDate(to, locale)}
        </span>
        <select value={weeks} onChange={(e) => setWeeks(Number(e.target.value))}>
          {[3, 6, 12].map((w) => (
            <option key={w} value={w}>
              {t("admin.content.weeksAhead", { n: w })}
            </option>
          ))}
        </select>
      </div>
      {q.isError ? <p className="error">{t("admin.common.error")}</p> : null}
      <table className="grid">
        <thead>
          <tr>
            <th>{t("admin.content.date")}</th>
            {LOCALES.map((loc) => (
              <th key={loc}>{t(`admin.common.${loc}`)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {days.map((day) => (
            <tr key={day} className={day === today ? "today" : day < today ? "past" : ""}>
              <td className="day">
                {fmtDate(day, locale, { weekday: "short", day: "2-digit", month: "2-digit" })}
                {day === today ? <span className="pill">{t("admin.content.today")}</span> : null}
              </td>
              {LOCALES.map((loc) => {
                const o = own(day, loc);
                const g = global(day, loc);
                const isEditing = editing?.day === day && editing.locale === loc;
                return (
                  <td key={loc} className={o ? "own" : g ? "global" : "empty"}>
                    {isEditing ? (
                      <DatedForm kind={kind} day={day} locale={loc} row={o} fallback={g} onClose={() => setEditing(null)} onSaved={invalidate} />
                    ) : (
                      <button className="cell" onClick={() => setEditing({ day, locale: loc })}>
                        {o ? (
                          <>
                            <span className="txt">{preview(kind, o)}</span>
                            {scope.churchId ? <span className="tag own">{t("admin.content.overlay")}</span> : null}
                          </>
                        ) : g ? (
                          <>
                            <span className="txt muted">{preview(kind, g)}</span>
                            <span className="tag">{t("admin.content.globalFallback")}</span>
                          </>
                        ) : (
                          <span className="muted">{t("admin.content.empty")}</span>
                        )}
                      </button>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function DatedForm({ kind, day, locale, row, fallback, onClose, onSaved }: { kind: DatedKind; day: string; locale: Locale; row?: DatedRow; fallback?: DatedRow; onClose: () => void; onSaved: () => void }) {
  const { t } = useT();
  const scope = useScope();
  const seed = row ?? fallback;
  const [f, setF] = useState<DatedRow>({ id: row?.id ?? "", churchId: scope.churchId, locale, publishedOn: day, verseRef: seed?.verseRef ?? "", verseText: seed?.verseText ?? "", impulse: seed?.impulse ?? "", voice: seed?.voice ?? "", title: seed?.title ?? "", body: seed?.body ?? "", durationSec: seed?.durationSec ?? 60, text: seed?.text ?? "" });
  const [flash, run, busy] = useFlash();
  const set = (k: keyof DatedRow) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });
  const sq = scopeQuery(scope, "scope");

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const body =
      kind === "daily-words"
        ? { locale, publishedOn: day, verseRef: f.verseRef, verseText: f.verseText, impulse: f.impulse, voice: f.voice || null }
        : kind === "daily-prayers"
          ? { locale, publishedOn: day, title: f.title, body: f.body, durationSec: Number(f.durationSec) || 60 }
          : { locale, publishedOn: day, text: f.text };
    if (await run(() => api(`/admin/content/${kind}${sq}`, { method: "PUT", json: body }))) {
      onSaved();
      onClose();
    }
  };
  const del = async () => {
    if (row && (await run(() => api(`/admin/content/${kind}/${row.id}`, { method: "DELETE" })))) {
      onSaved();
      onClose();
    }
  };

  return (
    <form className="inline-form" onSubmit={save}>
      {kind === "daily-words" ? (
        <>
          <Field label={t("admin.content.verseRef")}>
            <input required value={f.verseRef} onChange={set("verseRef")} />
          </Field>
          <Field label={t("admin.content.verseText")}>
            <textarea required rows={3} value={f.verseText} onChange={set("verseText")} />
          </Field>
          <Field label={t("admin.content.impulse")}>
            <textarea required rows={4} value={f.impulse} onChange={set("impulse")} />
          </Field>
          <Field label={t("admin.content.voice")}>
            <input value={f.voice ?? ""} onChange={set("voice")} />
          </Field>
        </>
      ) : kind === "daily-prayers" ? (
        <>
          <Field label={t("admin.content.prayerTitle")}>
            <input required value={f.title} onChange={set("title")} />
          </Field>
          <Field label={t("admin.content.prayerBody")}>
            <textarea required rows={6} value={f.body} onChange={set("body")} />
          </Field>
          <Field label={t("admin.content.durationSec")}>
            <input type="number" min={30} max={600} value={f.durationSec} onChange={set("durationSec")} />
          </Field>
        </>
      ) : (
        <Field label={t("admin.content.text")}>
          <textarea required rows={3} maxLength={280} value={f.text} onChange={set("text")} />
        </Field>
      )}
      <div className="actions">
        <button className="btn small" disabled={busy}>
          {t("admin.common.save")}
        </button>
        <button type="button" className="btn small ghost" onClick={onClose}>
          {t("admin.common.cancel")}
        </button>
        {row ? <ConfirmDelete onConfirm={del} /> : null}
      </div>
      <Msg msg={flash} />
    </form>
  );
}

/* ------------------------------------------------------------------ paths */

export interface PathRow {
  id: string;
  churchId: string | null;
  slug: string;
  title: Localized;
  description: Localized | null;
  theme: string;
  coverImage: string | null;
  sessionPoints: number;
  certificatePoints: number;
  position: number;
  published: boolean;
  sessionCount?: number;
  learners?: number;
  certificates?: number;
}

function Paths() {
  const { t, locale } = useT();
  const scope = useScope();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const q = useQuery({ queryKey: ["admin", "content", "paths", scope.churchId], queryFn: () => api<{ paths: PathRow[] }>(`/admin/content/paths${scopeQuery(scope, "scope")}`) });

  const togglePublish = useMutation({
    mutationFn: ({ id, published }: { id: string; published: boolean }) =>
      api(`/admin/content/paths/${id}`, { method: "PATCH", json: { published } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "content", "paths"] });
    },
  });

  return (
    <section className="panel">
      <div className="toolbar">
        <span className="muted">{q.data ? t("admin.content.sessions", { n: q.data.paths.reduce((a, p) => a + (p.sessionCount ?? 0), 0) }) : ""}</span>
        <button className="btn small" onClick={() => setCreating(true)}>
          {t("admin.content.newPath")}
        </button>
      </div>
      {creating ? <PathForm onClose={() => setCreating(false)} onSaved={() => qc.invalidateQueries({ queryKey: ["admin", "content", "paths"] })} /> : null}
      {q.data?.paths.length === 0 ? <p className="muted">{t("admin.content.noRows")}</p> : null}
      <div className="cards">
        {q.data?.paths.map((p) => (
          <Link
            key={p.id}
            to="/admin/content/paths/$id"
            params={{ id: p.id }}
            className={`pathcard ${p.theme} ${!p.published ? "is-hidden" : ""}`}
            style={p.theme?.startsWith("#") ? { borderColor: p.theme } : undefined}
          >
            <span className="eyebrow">{p.slug}</span>
            <strong>{pickText(p.title, locale)}</strong>
            <span className="muted">
              {t("admin.content.sessions", { n: p.sessionCount ?? 0 })} · {t("admin.content.learners", { n: p.learners ?? 0 })} · {t("admin.content.certificates", { n: p.certificates ?? 0 })}
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, gap: 8 }}>
              <span className={`pill ${p.published ? "live" : "warn"}`}>
                {p.published ? t("admin.content.pathVisible") : t("admin.content.pathHidden")}
              </span>
              <button
                type="button"
                className={`btn small ${p.published ? "ghost" : "secondary"}`}
                style={{ fontSize: 11, padding: "3px 8px", height: "auto", minHeight: 0 }}
                title={p.published ? t("admin.content.pathVisibleHint") : t("admin.content.pathHiddenHint")}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  togglePublish.mutate({ id: p.id, published: !p.published });
                }}
              >
                {p.published ? t("admin.content.hidePath") : t("admin.content.showPath")}
              </button>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

export function PathForm({ path, onClose, onSaved }: { path?: PathRow; onClose: () => void; onSaved: () => void }) {
  const { t } = useT();
  const scope = useScope();
  const [f, setF] = useState<Partial<PathRow>>(path ?? { slug: "", title: {}, description: {}, theme: "indigo", sessionPoints: 25, certificatePoints: 100, position: 0, published: false });
  const slugField = useSlug(path?.slug ?? "");
  const [flash, run, busy] = useFlash();
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const ok = await run(async () => {
      const slug = slugify(slugField.slug, { trim: true });
      if (!isValidSlug(slug)) throw new Error(t("admin.errors.slug"));
      const title = f.title ?? {};
      if (!title.de?.trim()) throw new Error(t("admin.errors.required", { field: t("admin.errors.fields.title_de") }));
      if (!title.en?.trim()) throw new Error(t("admin.errors.required", { field: t("admin.errors.fields.title_en") }));
      const sessionPoints = Number(f.sessionPoints);
      const certificatePoints = Number(f.certificatePoints);
      if (!Number.isInteger(sessionPoints) || sessionPoints < 0 || sessionPoints > 1000) throw new Error(t("admin.errors.codes.session_points_range"));
      if (!Number.isInteger(certificatePoints) || certificatePoints < 0 || certificatePoints > 5000) throw new Error(t("admin.errors.codes.certificate_points_range"));
      const body = { slug, title, description: f.description, theme: f.theme, sessionPoints, certificatePoints, position: Number(f.position), published: Boolean(f.published) };
      return path ? api(`/admin/content/paths/${path.id}`, { method: "PATCH", json: body }) : api(`/admin/content/paths${scopeQuery(scope, "scope")}`, { method: "POST", json: body });
    }, t("admin.common.saved"));
    if (ok) {
      onSaved();
      if (!path) onClose();
    }
  };
  return (
    <form className="card form" onSubmit={save}>
      <div className="two-col">
        <Field label={t("admin.content.slug")}>
          <SlugInput value={slugField.slug} onValue={slugField.setSlugInput} onBlurValue={slugField.blurSlug} />
        </Field>
        <Field label={t("admin.content.theme")}>
          <ColorPicker value={f.theme} onChange={(theme) => setF({ ...f, theme })} />
        </Field>
      </div>
      <LocalizedField
        label={t("admin.content.pathTitle")}
        required
        value={f.title}
        onChange={(title) => {
          setF({ ...f, title });
          slugField.syncFrom(title.de || title.en || "");
        }}
      />
      <LocalizedField label={t("admin.content.description")} multiline value={f.description} onChange={(description) => setF({ ...f, description })} />
      <div className="two-col">
        <Field label={t("admin.content.sessionPoints")}>
          <input type="number" min={0} max={1000} value={f.sessionPoints ?? 0} onChange={(e) => setF({ ...f, sessionPoints: Number(e.target.value) })} />
        </Field>
        <Field label={t("admin.content.certificatePoints")}>
          <input type="number" min={0} max={5000} value={f.certificatePoints ?? 0} onChange={(e) => setF({ ...f, certificatePoints: Number(e.target.value) })} />
        </Field>
        <Field label={t("admin.common.position")}>
          <input type="number" min={0} value={f.position ?? 0} onChange={(e) => setF({ ...f, position: Number(e.target.value) })} />
        </Field>
      </div>
      <div style={{ margin: "14px 0" }}>
        <Toggle
          label={f.published ? t("admin.content.pathVisible") : t("admin.content.pathHidden")}
          checked={Boolean(f.published)}
          onChange={(published) => setF({ ...f, published })}
        />
        <div className="muted" style={{ fontSize: 12, marginLeft: 26, marginTop: 4 }}>
          {f.published ? t("admin.content.pathVisibleHint") : t("admin.content.pathHiddenHint")}
        </div>
      </div>
      <div className="actions">
        <button className="btn small" disabled={busy}>
          {t("admin.common.save")}
        </button>
        <button type="button" className="btn small ghost" onClick={onClose}>
          {path ? t("admin.common.close") : t("admin.common.cancel")}
        </button>
      </div>
      <Msg msg={flash} />
    </form>
  );
}

/* ------------------------------------------------------------------ shots */

interface ShotRow {
  id: string;
  churchId: string | null;
  title: Localized;
  videoId: string;
  durationSec: number | null;
  theme: string;
  position: number;
  published: boolean;
}

function Shots() {
  const { t, locale } = useT();
  const scope = useScope();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<ShotRow | "new" | null>(null);
  const q = useQuery({ queryKey: ["admin", "content", "shots", scope.churchId], queryFn: () => api<{ shots: ShotRow[] }>(`/admin/content/shots${scopeQuery(scope, "scope")}`) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "content", "shots"] });
  const del = useMutation({ mutationFn: (id: string) => api(`/admin/content/shots/${id}`, { method: "DELETE" }), onSuccess: invalidate });

  return (
    <section className="panel">
      <div className="toolbar">
        <span />
        <button className="btn small" onClick={() => setEditing("new")}>
          {t("admin.content.newShot")}
        </button>
      </div>
      {editing ? <ShotForm shot={editing === "new" ? undefined : editing} onClose={() => setEditing(null)} onSaved={invalidate} /> : null}
      {q.data?.shots.length === 0 ? <p className="muted">{t("admin.content.noRows")}</p> : null}
      <div className="list">
        {q.data?.shots.map((s) => (
          <div key={s.id} className="listrow">
            <img src={`https://i.ytimg.com/vi/${s.videoId}/mqdefault.jpg`} alt="" width={96} height={54} />
            <div className="grow">
              <strong>{pickText(s.title, locale)}</strong>
              <div className="muted">
                {s.videoId} · {s.durationSec ? `${s.durationSec}s` : ""} · {s.theme}
              </div>
            </div>
            <span className={`pill ${s.published ? "live" : ""}`}>{s.published ? t("admin.common.published") : t("admin.common.draft")}</span>
            <button className="btn small ghost" onClick={() => setEditing(s)}>
              {t("admin.common.edit")}
            </button>
            <ConfirmDelete onConfirm={() => del.mutate(s.id)} />
          </div>
        ))}
      </div>
    </section>
  );
}

function ShotForm({ shot, onClose, onSaved }: { shot?: ShotRow; onClose: () => void; onSaved: () => void }) {
  const { t } = useT();
  const scope = useScope();
  const [f, setF] = useState<Partial<ShotRow>>(shot ?? { title: {}, videoId: "", durationSec: null, theme: "carmine", position: 0, published: true });
  const [flash, run, busy] = useFlash();
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const body = { title: f.title, videoId: f.videoId, durationSec: f.durationSec ? Number(f.durationSec) : null, theme: f.theme, position: Number(f.position ?? 0), published: Boolean(f.published) };
    if (await run(() => (shot ? api(`/admin/content/shots/${shot.id}`, { method: "PATCH", json: body }) : api(`/admin/content/shots${scopeQuery(scope, "scope")}`, { method: "POST", json: body })))) {
      onSaved();
      onClose();
    }
  };
  return (
    <form className="card form" onSubmit={save}>
      <LocalizedField label={t("admin.content.shotTitle")} required value={f.title} onChange={(title) => setF({ ...f, title })} />
      <div className="two-col">
        <Field label={t("admin.content.videoId")}>
          <input required value={f.videoId ?? ""} onChange={(e) => setF({ ...f, videoId: e.target.value })} />
        </Field>
        <Field label={t("admin.content.durationSec")}>
          <input type="number" min={1} value={f.durationSec ?? ""} onChange={(e) => setF({ ...f, durationSec: e.target.value ? Number(e.target.value) : null })} />
        </Field>
        <Field label={t("admin.content.theme")}>
          <select value={f.theme} onChange={(e) => setF({ ...f, theme: e.target.value })}>
            {THEMES.map((th) => (
              <option key={th}>{th}</option>
            ))}
          </select>
        </Field>
        <Field label={t("admin.common.position")}>
          <input type="number" min={0} value={f.position ?? 0} onChange={(e) => setF({ ...f, position: Number(e.target.value) })} />
        </Field>
      </div>
      <Toggle label={t("admin.common.published")} checked={Boolean(f.published)} onChange={(published) => setF({ ...f, published })} />
      <div className="actions">
        <button className="btn small" disabled={busy}>
          {t("admin.common.save")}
        </button>
        <button type="button" className="btn small ghost" onClick={onClose}>
          {t("admin.common.cancel")}
        </button>
      </div>
      <Msg msg={flash} />
    </form>
  );
}

/* ------------------------------------------------------------------ seasons */

interface SeasonRow {
  id: string;
  churchId: string | null;
  slug: string;
  title: Localized;
  subtitle: Localized | null;
  startsOn: string;
  weeks: number;
  pathId: string | null;
  active: boolean;
}

function Seasons() {
  const { t, locale } = useT();
  const scope = useScope();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<SeasonRow | "new" | null>(null);
  const q = useQuery({ queryKey: ["admin", "content", "seasons", scope.churchId], queryFn: () => api<{ seasons: SeasonRow[] }>(`/admin/content/seasons${scopeQuery(scope, "scope")}`) });
  const paths = useQuery({ queryKey: ["admin", "content", "paths", scope.churchId], queryFn: () => api<{ paths: PathRow[] }>(`/admin/content/paths${scopeQuery(scope, "scope")}`) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "content", "seasons"] });
  const del = useMutation({ mutationFn: (id: string) => api(`/admin/content/seasons/${id}`, { method: "DELETE" }), onSuccess: invalidate });
  const pathTitle = (id: string | null) => (id ? pickText(paths.data?.paths.find((p) => p.id === id)?.title, locale) : t("admin.content.noPath"));

  return (
    <section className="panel">
      <div className="toolbar">
        <span />
        <button className="btn small" onClick={() => setEditing("new")}>
          {t("admin.content.newSeason")}
        </button>
      </div>
      {editing ? <SeasonForm season={editing === "new" ? undefined : editing} paths={paths.data?.paths ?? []} onClose={() => setEditing(null)} onSaved={invalidate} /> : null}
      {q.data?.seasons.length === 0 ? <p className="muted">{t("admin.content.noRows")}</p> : null}
      <div className="list">
        {q.data?.seasons.map((s) => (
          <div key={s.id} className="listrow">
            <div className="grow">
              <strong>{pickText(s.title, locale)}</strong>
              <div className="muted">
                {s.slug} · {fmtDate(s.startsOn, locale)} · {s.weeks} {t("admin.content.weeks")} · {pathTitle(s.pathId)}
              </div>
            </div>
            <span className={`pill ${s.active ? "live" : ""}`}>{s.active ? t("admin.content.active") : t("admin.common.draft")}</span>
            <button className="btn small ghost" onClick={() => setEditing(s)}>
              {t("admin.common.edit")}
            </button>
            <ConfirmDelete onConfirm={() => del.mutate(s.id)} />
          </div>
        ))}
      </div>
    </section>
  );
}

function SeasonForm({ season, paths, onClose, onSaved }: { season?: SeasonRow; paths: PathRow[]; onClose: () => void; onSaved: () => void }) {
  const { t, locale } = useT();
  const scope = useScope();
  const [f, setF] = useState<Partial<SeasonRow>>(season ?? { slug: "", title: {}, subtitle: {}, startsOn: isoDay(new Date()), weeks: 6, pathId: null, active: false });
  const slugField = useSlug(season?.slug ?? "");
  const [flash, run, busy] = useFlash();
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      await run(async () => {
        const slug = slugify(slugField.slug, { trim: true });
        if (!isValidSlug(slug)) throw new Error(t("admin.errors.slug"));
        const body = { slug, title: f.title, subtitle: f.subtitle, startsOn: f.startsOn, weeks: Number(f.weeks), pathId: f.pathId || null, active: Boolean(f.active) };
        return season ? api(`/admin/content/seasons/${season.id}`, { method: "PATCH", json: body }) : api(`/admin/content/seasons${scopeQuery(scope, "scope")}`, { method: "POST", json: body });
      })
    ) {
      onSaved();
      onClose();
    }
  };
  return (
    <form className="card form" onSubmit={save}>
      <div className="two-col">
        <Field label={t("admin.content.slug")}>
          <SlugInput value={slugField.slug} onValue={slugField.setSlugInput} onBlurValue={slugField.blurSlug} />
        </Field>
        <Field label={t("admin.content.startsOn")}>
          <input type="date" required value={f.startsOn ?? ""} onChange={(e) => setF({ ...f, startsOn: e.target.value })} />
        </Field>
        <Field label={t("admin.content.weeks")}>
          <input type="number" min={1} max={20} value={f.weeks ?? 6} onChange={(e) => setF({ ...f, weeks: Number(e.target.value) })} />
        </Field>
        <Field label={t("admin.content.linkedPath")}>
          <select value={f.pathId ?? ""} onChange={(e) => setF({ ...f, pathId: e.target.value || null })}>
            <option value="">{t("admin.content.noPath")}</option>
            {paths.map((p) => (
              <option key={p.id} value={p.id}>
                {pickText(p.title, locale)}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <LocalizedField
        label={t("admin.content.seasonTitle")}
        required
        value={f.title}
        onChange={(title) => {
          setF({ ...f, title });
          slugField.syncFrom(title.de || title.en || "");
        }}
      />
      <LocalizedField label={t("admin.content.subtitle")} value={f.subtitle} onChange={(subtitle) => setF({ ...f, subtitle })} />
      <Toggle label={t("admin.content.active")} checked={Boolean(f.active)} onChange={(active) => setF({ ...f, active })} />
      <div className="actions">
        <button className="btn small" disabled={busy}>
          {t("admin.common.save")}
        </button>
        <button type="button" className="btn small ghost" onClick={onClose}>
          {t("admin.common.cancel")}
        </button>
      </div>
      <Msg msg={flash} />
    </form>
  );
}
